// frontend/src/components/GetStartedCard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './GetStartedCard.css';

const STEP_ICONS = {
  settings: (
    <svg width="22" height="22" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16.167 12.5a1.375 1.375 0 00.275 1.517l.05.05a1.667 1.667 0 11-2.359 2.358l-.05-.05a1.375 1.375 0 00-1.516-.275 1.375 1.375 0 00-.834 1.258v.142a1.667 1.667 0 11-3.333 0v-.075a1.375 1.375 0 00-.9-1.258 1.375 1.375 0 00-1.517.275l-.05.05a1.667 1.667 0 11-2.358-2.359l.05-.05a1.375 1.375 0 00.275-1.516 1.375 1.375 0 00-1.258-.834h-.142a1.667 1.667 0 110-3.333h.075a1.375 1.375 0 001.258-.9 1.375 1.375 0 00-.275-1.517l-.05-.05a1.667 1.667 0 112.358-2.358l.05.05a1.375 1.375 0 001.517.275h.066a1.375 1.375 0 00.834-1.258v-.142a1.667 1.667 0 013.333 0v.075a1.375 1.375 0 00.834 1.258 1.375 1.375 0 001.516-.275l.05-.05a1.667 1.667 0 112.359 2.358l-.05.05a1.375 1.375 0 00-.275 1.517v.066a1.375 1.375 0 001.258.834h.142a1.667 1.667 0 010 3.333h-.075a1.375 1.375 0 00-1.258.834z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  competitors: (
    <svg width="22" height="22" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14.167 17.5v-1.667a3.333 3.333 0 00-3.334-3.333H5.833a3.333 3.333 0 00-3.333 3.333V17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8.333 9.167a3.333 3.333 0 100-6.667 3.333 3.333 0 000 6.667zM17.5 17.5v-1.667a3.333 3.333 0 00-2.5-3.225M12.5 2.608a3.333 3.333 0 010 6.45" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  reviews: (
    <svg width="22" height="22" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 1.667l2.575 5.216 5.758.842-4.166 4.058.983 5.734L10 14.808l-5.15 2.709.983-5.734-4.166-4.058 5.758-.842L10 1.667z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

const CHECK_ICON = (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.333 4L6 11.333 2.667 8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function GetStartedCard() {
  const [onboarding, setOnboarding] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const wasDismissed = localStorage.getItem('onboarding_dismissed');
    if (wasDismissed === 'true') {
      setDismissed(true);
      setLoading(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        // Must go through the api service: the API lives on a different host
        // (REACT_APP_API_URL). A relative '/api/...' fetch hits the SPA fallback,
        // which returns the shell as 200 text/html, so res.ok is true, res.json()
        // throws, and this card silently never renders.
        const data = await api.request('/onboarding/status');
        if (data) {
          setOnboarding(data);
          if (data.all_complete) {
            const seenComplete = localStorage.getItem('onboarding_seen_complete');
            if (seenComplete === 'true') {
              setDismissed(true);
            } else {
              localStorage.setItem('onboarding_seen_complete', 'true');
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch onboarding status:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('onboarding_dismissed', 'true');
  };

  const handleStepClick = (step) => {
    if (step.completed) return;
    // Deep-link to the review link section of settings
    if (step.key === 'settings') {
      navigate('/settings#review-link');
    } else {
      navigate(step.link);
    }
  };

  if (loading || dismissed || !onboarding) return null;

  const { steps, completed_count, total_steps, all_complete } = onboarding;
  const progressPercent = (completed_count / total_steps) * 100;

  return (
    <div className={`get-started-card ${all_complete ? 'all-complete' : ''}`}>
      <div className="get-started-header">
        <div className="get-started-header-left">
          <h3 className="get-started-title">
            {all_complete ? (
              <>
                <span className="get-started-title-icon">&#10003;</span>
                You're all set!
              </>
            ) : (
              'Get started with StellarReply'
            )}
          </h3>
          <p className="get-started-subtitle">
            {all_complete
              ? 'Your account is fully configured. Happy reviewing!'
              : `${completed_count} of ${total_steps} steps complete`}
          </p>
        </div>
        <button
          className="get-started-dismiss"
          onClick={handleDismiss}
          title={all_complete ? 'Close' : 'Dismiss'}
          aria-label="Dismiss getting started guide"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.5 4.5L4.5 13.5M4.5 4.5l9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      <div className="get-started-progress-track">
        <div
          className="get-started-progress-bar"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <div className="get-started-steps">
        {steps.map((step, index) => (
          <button
            key={step.key}
            className={`get-started-step ${step.completed ? 'completed' : ''}`}
            onClick={() => handleStepClick(step)}
            disabled={step.completed}
          >
            <div className="step-indicator">
              {step.completed ? (
                <div className="step-check">{CHECK_ICON}</div>
              ) : (
                <div className="step-number">{index + 1}</div>
              )}
            </div>
            <div className="step-icon">{STEP_ICONS[step.key]}</div>
            <div className="step-content">
              <span className="step-title">{step.title}</span>
              <span className="step-description">{step.description}</span>
            </div>
            {!step.completed && (
              <div className="step-arrow">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
